import { Observable, startWith, filter, map, distinctUntilChanged, switchMap, of, shareReplay } from "rxjs";
import { ChangesDetection } from "../../../../change-detection";
import { I_NoticeResponse, I_StaffInfo } from "../api/notices.response";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";

export type T_NoticeListChange =
  | { action: E_ChangeAction.Add; noticeId: string; data: C_NoticeItem }
  | { action: E_ChangeAction.Update; noticeId: string; data: I_NoticeResponse }
  | { action: E_ChangeAction.Delete; noticeId: string }
  | { action: E_ChangeAction.AddBulk; affected: Set<string> }
  | { action: E_ChangeAction.Reset };


export class C_NoticeList extends ChangesDetection<T_NoticeListChange> {
  private newList: Map<string, C_NoticeItem> = new Map();
  private cachedSubs = new Map<string, Observable<C_NoticeItem | undefined>>();

  constructor(json?: I_NoticeResponse) {
    super();
    if (!json) return;
    this.addNewNotice(json);
  }

  size(): number {
    return this.newList.size;
  }

  getValues(): C_NoticeItem[] {
    return Array.from(this.newList.values());
  }

  getNotice(id: string): C_NoticeItem | undefined {
    return this.newList.get(id);
  }

  addNewNotice(a: I_NoticeResponse) {
    if (!a.id) return;
    if (this.newList.has(a.id)) {
      this.updateNotice(a);
      return;
    }

    const newNotice = new C_NoticeItem(a);
    this.newList.set(a.id, newNotice);
    this.forceUpdateDetection({
      action: E_ChangeAction.Add,
      noticeId: a.id,
      data: newNotice
    });
  }

  updateNotice(a: I_NoticeResponse) {
    if (!a.id) return;
    const notice = this.newList.get(a.id);
    if (!notice) {
      const newNotice = new C_NoticeItem(a);
      this.newList.set(a.id, newNotice);
      this.forceUpdateDetection({
        action: E_ChangeAction.Add,
        noticeId: a.id,
        data: newNotice
      });
      return;
    }

    notice.initializeFromJson(a);
    this.forceUpdateDetection({
      action: E_ChangeAction.Update,
      noticeId: a.id,
      data: a
    });
  }

  deleteNotice(a: I_NoticeResponse | string) {
    const id = typeof a === 'string' ? a : a?.id;
    if (!id) return;

    if (this.newList.has(id)) {
      this.newList.delete(id);
      this.forceUpdateDetection({
        action: E_ChangeAction.Delete,
        noticeId: id
      });
    }
  }

  setNoticesBulk(noticeList: I_NoticeResponse[]) {
    const affected = new Set<string>();

    noticeList.forEach(a => {
      if (!a.id) return;
      affected.add(a.id);

      const current = this.newList.get(a.id);
      if (current) {
        current.initializeFromJson(a);
      } else {
        this.newList.set(a.id, new C_NoticeItem(a));
      }
    });

    this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected });
  }

  resetNotice() {
    this.newList.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }

  getLiveNotice(noticeId: string): Observable<C_NoticeItem | undefined> {
    const cacheKey = noticeId;
    if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      filter((change) => {
        if (!change) return true; // Carga/disparo inicial
        if (change.action === E_ChangeAction.Reset) return true;
        if (change.action === E_ChangeAction.AddBulk) return change.affected.has(noticeId);

        return change.noticeId === noticeId;
      }),
      map(() => this.newList.get(noticeId)),
      distinctUntilChanged(),
      switchMap(item => {
        if (!item) return of(undefined);

        // Escuchamos los cambios de propiedades del propio aviso (isFooterEnabled, staffInfo, etc.)
        return item.getForcedChanges().pipe(
          startWith(item),
          map(() => item)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(cacheKey, stream$);
    return stream$;
  }

  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }
}

export class C_NoticeItem extends ChangesDetection {

  id?: string
  noticeDate?: Date;
  description?: string;
  noticeType?: string;
  displayId?: string
  staffInfoList?: I_StaffInfo[]
  isFooterEnabled: boolean = true

  constructor(json?: I_NoticeResponse) {
    super();
    if (!json) return;
    this.initializeFromJson(json);
  }

  initializeFromJson(info?: I_NoticeResponse) {
    if (!info) return;
    if (info.id) this.id = info.id;
    if (info.noticeDate) this.noticeDate = new Date(info.noticeDate);
    if (info.description) this.description = info.description;
    if (info.noticeType) this.noticeType = info.noticeType;
    if (info.displayId) this.displayId = info.displayId;
    if (info.staffInfoList) this.staffInfoList = info.staffInfoList;
    if (info.isFooterEnabled !== undefined) this.isFooterEnabled = info.isFooterEnabled;

    // Disparador reactivo interno para que cualquier "getLiveNotice" se entere de los cambios de propiedades.
    this.forceUpdateDetection();
  }

  clone(): C_NoticeItem {
    const clone = new C_NoticeItem();
    clone.id = this.id;
    clone.noticeDate = this.noticeDate ? new Date(this.noticeDate) : undefined;
    clone.description = this.description;
    clone.noticeType = this.noticeType;
    clone.displayId = this.displayId;
    clone.staffInfoList = this.staffInfoList ? [...this.staffInfoList] : undefined;
    clone.isFooterEnabled = this.isFooterEnabled;
    return clone;
  }

}